import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Phone, MapPin, Clock, MessageSquare } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { createContactRequest } from '../api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await createContactRequest(formData);
      toast({
        title: "Discovery Call Requested!",
        description: `Reference: ${response.ticketId}. ACE will contact you within one business day.`
      });
      setFormData({ name: '', email: '', phone: '', serviceType: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const situations = [
    { value: 'executive-transport', label: 'I need executive transportation' },
    { value: 'vehicle-sourcing', label: 'I want to source / purchase a vehicle' },
    { value: 'airport-event', label: 'I need airport or event transportation' },
    { value: 'membership', label: "I'm interested in ACE membership" },
    { value: 'corporate', label: 'I need corporate / B2B services' },
    { value: 'advisory', label: 'I want auto lifestyle advisory' },
    { value: 'other', label: 'Something else' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      {/* Hero */}
      <div className="bg-black text-white py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-red-500 font-semibold tracking-widest mb-4">CONTACT</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" data-testid="contact-hero-title">
            Let's talk about <em className="text-red-500 not-italic">your auto lifestyle.</em>
          </h1>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto">
            Every ACE engagement begins with a conversation. Book a free Discovery Call or reach us directly. No pressure. No pitch. Just clarity.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16 overflow-hidden">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info - Left Column */}
          <div className="lg:col-span-2 space-y-6 min-w-0">
            <h2 className="text-2xl font-bold text-black mb-6">Reach Us Directly</h2>
            
            <Card className="p-5 border-gray-200">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                  <Phone className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-bold text-black text-sm">Business Phone</h3>
                  <a href="tel:6788463009" className="text-gray-700 hover:text-red-600 transition-colors">(678) 846-3009</a>
                  <p className="text-xs text-gray-500 mt-1">Call or text — ACE responds promptly</p>
                </div>
              </div>
            </Card>

            <Card className="p-5 border-gray-200">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-5 w-5 text-red-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-black text-sm">Business Email</h3>
                  <a href="mailto:JD@YOURAUTOCONCIERGE.COM" className="text-gray-700 hover:text-red-600 transition-colors break-all">JD@YourAutoConcierge.com</a>
                  <p className="text-xs text-gray-500 mt-1">Direct to Your Concierge</p>
                </div>
              </div>
            </Card>

            <Card className="p-5 border-gray-200">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-bold text-black text-sm">Location</h3>
                  <p className="text-gray-700">Sandy Springs, GA 30350</p>
                  <p className="text-xs text-gray-500 mt-1">Serving Metro Atlanta & ATL Hartsfield-Jackson</p>
                </div>
              </div>
            </Card>

            <Card className="p-5 border-gray-200">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="h-5 w-5 text-red-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-black text-sm">Personal / Intake</h3>
                  <a href="mailto:yourautoconcierge@gmail.com" className="text-gray-700 hover:text-red-600 transition-colors text-sm break-all">yourautoconcierge@gmail.com</a>
                  <p className="text-xs text-gray-500 mt-1"><a href="tel:6788463009" className="hover:text-red-600 transition-colors">678-846-3009</a></p>
                </div>
              </div>
            </Card>

            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Discovery Calls</p>
                <p className="font-bold text-black text-sm">Free — 20 Min</p>
                <p className="text-xs text-gray-500">By appointment</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Response Time</p>
                <p className="font-bold text-black text-sm">Same Day</p>
                <p className="text-xs text-gray-500">Mon–Sat</p>
              </div>
            </div>
          </div>

          {/* Discovery Call Form - Right Column */}
          <div className="lg:col-span-3">
            <Card className="p-8 border-gray-200">
              <h2 className="text-2xl font-bold text-black mb-2">Book a Discovery Call</h2>
              <p className="text-sm text-gray-600 mb-8">Tell us about your auto lifestyle situation and we'll schedule your free 20-minute Discovery Call with your concierge.</p>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-black mb-2 block">First Name *</Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="First Name"
                      className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-black mb-2 block">Last Name</Label>
                    <Input
                      placeholder="Last Name"
                      className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-black mb-2 block">Email Address *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <Label className="text-black mb-2 block">Phone Number *</Label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <Label className="text-black mb-2 block">What Best Describes Your Situation? *</Label>
                  <Select 
                    value={formData.serviceType} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, serviceType: value }))}
                  >
                    <SelectTrigger className="border-gray-200 focus:border-red-600 focus:ring-red-600" data-testid="contact-situation-select">
                      <SelectValue placeholder="Select one..." />
                    </SelectTrigger>
                    <SelectContent>
                      {situations.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-black mb-2 block">Tell Us More</Label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                    placeholder="Describe your auto lifestyle needs, goals, or questions..."
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-12 bg-red-600 hover:bg-red-700 text-white text-lg"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit — Request My Discovery Call'}
                </Button>
                <p className="text-xs text-gray-500 text-center">ACE will contact you within one business day to confirm your call time.</p>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
